import React, { useState } from "react";

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<"clients" | "pilots">("clients");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-mediumRound shadow-lg max-w-4xl w-full mx-4 p-8 relative max-h-[90vh] overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 z-10"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary mb-4">Terms & Conditions</h1>
          
          {/* Tabs */}
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab("clients")}
              className={`px-4 py-2 font-medium ${
                activeTab === "clients"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              For Clients
            </button>
            <button
              onClick={() => setActiveTab("pilots")}
              className={`px-4 py-2 font-medium ${
                activeTab === "pilots"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              For Pilots
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto pr-4">
          {activeTab === "clients" && (
            <div className="space-y-6 text-sm text-gray-700 leading-relaxed">
              <div>
                <h1 className="text-xl font-bold text-primary mb-2">Terms of Service – Clients</h1>
                <p className="text-base font-semibold text-gray-700 mb-4">Last updated: 2 July 2025</p>
              </div>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">1. Introduction</h2>
                <p className="mb-3">
                  These Terms of Service ("Client Terms") govern your use of the DroneDen platform operated by{" "}
                  <strong>ACEVISION CONSULTING S.R.L.</strong> ("DroneDen", "we", "us").
                </p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• <strong>Company Details:</strong> Str. Mihail Kogălniceanu Nr. 4E, Sat Florești, Jud. Cluj, Romania</li>
                  <li>• <strong>Registration:</strong> CUI 46912927, J12/6029/2022</li>
                  <li>• <strong>Platform:</strong> <a href="https://www.droneden.ro" className="text-primary underline" target="_blank" rel="noopener noreferrer">www.droneden.ro</a></li>
                </ul>
                <p>By accessing our platform or booking a service, you agree to these Terms.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">2. Platform Purpose</h2>
                <p className="mb-3">DroneDen is a marketplace connecting Clients with independent drone Pilots. We do not provide aerial services ourselves.</p>
                
                <div className="mb-3">
                  <p className="font-medium mb-2">You:</p>
                  <ul className="text-sm space-y-1 ml-6">
                    <li>• Are entering a direct agreement with the Pilot you choose</li>
                    <li>• Are fully responsible for any services you contract</li>
                    <li>• Acknowledge that DroneDen is not part of that service contract</li>
                  </ul>
                </div>

                <div>
                  <p className="font-medium mb-2">DroneDen:</p>
                  <ul className="text-sm space-y-1 ml-6">
                    <li>• Is not liable for Pilot performance or job outcomes</li>
                    <li>• Does not supervise or control Pilots</li>
                    <li>• Cannot guarantee payments or service quality</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">3. Account & Bookings</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• You must provide accurate details and a valid payment method.</li>
                  <li>• A booking becomes <strong>binding</strong> once payment is completed.</li>
                  <li>• All pricing includes VAT where applicable.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">4. Platform Access & Use</h2>
                <p className="mb-3">You are granted a <strong>revocable, non-transferable licence</strong> to view the Platform for personal, non-commercial use.</p>
                
                <p className="font-medium mb-2">You must <strong>not</strong>:</p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• Scrape, reverse-engineer, or decompile any part of the Platform</li>
                  <li>• Use DroneDen branding or trademarks without permission</li>
                  <li>• Spam, overload, or attempt to compromise the site</li>
                  <li>• Upload illegal, infringing, or harmful content</li>
                </ul>
                
                <p>Violation may lead to account suspension or legal action.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">5. Client Conduct</h2>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• Communicate professionally and truthfully</li>
                  <li>• Honor job agreements unless the Pilot breaches</li>
                  <li>• Use the platform messaging system for all job-related discussions</li>
                  <li>• Report any off-platform contact attempts</li>
                </ul>
                <p>You are liable for any unlawful or defamatory content you submit.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">6. No Circumvention</h2>
                <p>You may not hire or pay a Pilot outside of the Platform for any job initiated through DroneDen. Attempts must be reported.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">7. Payments & Fees</h2>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• <strong>Payment processor:</strong> Stripe Payments Europe, Ltd.</li>
                  <li>• <strong>Client payout:</strong> 80% goes to the Pilot</li>
                  <li>• <strong>DroneDen fee:</strong> 20% of total job cost</li>
                  <li>• Payments are released after client approval or automatically after 48 hours</li>
                </ul>
                <blockquote className="border-l-4 border-primary pl-4 italic text-sm text-gray-600">
                  Late payments may incur 1.5% monthly interest and funds may be withheld by Stripe.
                </blockquote>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">8. Cancellations & Refunds</h2>
                <p className="mb-2">Refunds may apply if:</p>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• The Pilot cancels or fails to show</li>
                  <li>• Fraudulent activity occurs</li>
                  <li>• You cancel in line with the booking policy</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">9. Your Responsibilities</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• Obtain necessary permits or property access unless agreed otherwise</li>
                  <li>• Understand that drone operations may be rescheduled due to weather</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">10. Ratings & Feedback</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• You may rate Pilots after jobs</li>
                  <li>• Feedback must be honest and not manipulated</li>
                  <li>• DroneDen may moderate or remove abusive reviews</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">11. Intellectual Property</h2>
                <p className="mb-3">All content on DroneDen is protected by copyright laws.</p>
                <p>By uploading content, you grant us a <strong>global, royalty-free licence</strong> to use it for platform operation and promotion.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">12. Copyright Infringement</h2>
                <p className="mb-3">If you believe your copyright is infringed, email <a href="mailto:contact@droneden.ro" className="text-primary underline">contact@droneden.ro</a> with:</p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• The content in question</li>
                  <li>• Its location on the site</li>
                  <li>• Your contact details</li>
                  <li>• A good-faith ownership declaration</li>
                </ul>
                <p>We will follow notice-and-takedown procedures under Law 365/2002.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">13. Disclaimers & Limitations</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• DroneDen provides the Platform "as-is" without guarantees</li>
                  <li>• We are not liable for damage or injury during drone operations</li>
                  <li>• Our total liability is limited to the DroneDen fee or RON 300</li>
                  <li>• No responsibility is accepted for indirect losses (profits, data, reputation)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">14. Indemnification</h2>
                <p>You agree to indemnify DroneDen for any third-party claims caused by your breach of these Terms or applicable laws.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">15. Platform Availability</h2>
                <p>We may modify or suspend features at any time without notice. We do not guarantee uninterrupted service.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">16. Legal Terms</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• <strong>Governing Law:</strong> Romanian law</li>
                  <li>• <strong>Jurisdiction:</strong> Cluj-Napoca courts, unless overridden by consumer laws</li>
                  <li>• These Terms, together with the Privacy Policy, form the entire agreement</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">17. Updates to Terms</h2>
                <p>We may revise these Terms periodically. Changes become effective upon publication and your continued use constitutes acceptance.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">18. Contact</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• 📧 <a href="mailto:contact@droneden.ro" className="text-primary underline">contact@droneden.ro</a></li>
                  <li>• 📞 +40 749 148 608</li>
                </ul>
              </section>
            </div>
          )}

          {activeTab === "pilots" && (
            <div className="space-y-6 text-sm text-gray-700 leading-relaxed">
              <div>
                <h1 className="text-xl font-bold text-primary mb-2">Terms of Service – Drone Pilots</h1>
                <p className="text-base font-semibold text-gray-700 mb-4">Last updated: 2 July 2025</p>
              </div>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">1. Introduction</h2>
                <p className="mb-3">
                  These Terms of Service ("Pilot Terms") form a binding agreement between you (the "Pilot") and{" "}
                  <strong>ACEVISION CONSULTING S.R.L.</strong> ("DroneDen", "we", "us").
                </p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• <strong>Company Info:</strong> Str. Mihail Kogălniceanu Nr. 4E, Sat Florești, Jud. Cluj, Romania</li>
                  <li>• <strong>Reg. No.:</strong> CUI 46912927, J12/6029/2022</li>
                  <li>• <strong>Platform:</strong> <a href="https://www.droneden.ro" className="text-primary underline" target="_blank" rel="noopener noreferrer">www.droneden.ro</a></li>
                </ul>
                <p>By using DroneDen, you agree to these Terms.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">2. Platform Purpose</h2>
                <p className="mb-3">
                  DroneDen is a digital marketplace for drone professionals to bid on client jobs. Each job contract ("Job Agreement") is solely between you and the client.
                </p>
                <blockquote className="border-l-4 border-primary pl-4 italic text-sm text-gray-600 mb-3">
                  We do <strong>not</strong> provide drone services directly and are <strong>not</strong> a party to any Job Agreement.
                </blockquote>
                <p>DroneDen reserves the right to cancel jobs that violate laws or platform policies.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">3. Eligibility</h2>
                <p className="mb-2">You must:</p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• Be 18+ years old</li>
                  <li>• Hold a valid EU/RO drone licence and insurance</li>
                  <li>• Operate legally as a <strong>PFA</strong> or <strong>SRL</strong></li>
                  <li>• Comply with EASA and Romanian CAA rules</li>
                </ul>
                <p>You confirm all uploaded content (portfolio, logs, etc.) is accurate and lawful.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">4. Account Registration</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• Provide truthful information</li>
                  <li>• Upload KYC documents if requested</li>
                  <li>• We may suspend accounts for non-compliance</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">5. Platform Use & Restrictions</h2>
                <p className="mb-3">You're granted a <strong>limited, non-transferable licence</strong> to use DroneDen for professional purposes only.</p>
                
                <p className="font-medium mb-2">Do <strong>not</strong>:</p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• Scrape, copy, reverse-engineer, or decompile</li>
                  <li>• Remove copyrights or trademarks</li>
                  <li>• Spam users or overload the platform</li>
                  <li>• Upload unlawful or harmful content</li>
                </ul>
                
                <p>Violation may lead to account suspension or legal action.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">6. Communications</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• All communication must take place through DroneDen messaging (in English or Romanian)</li>
                  <li>• Report any off-platform contact attempts immediately</li>
                  <li>• Check your messages regularly and respond professionally</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">7. No Circumvention</h2>
                <p className="mb-2">You may <strong>not</strong>:</p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• Accept payment outside the platform</li>
                  <li>• Bypass DroneDen or Stripe Connect</li>
                </ul>
                <p>Doing so may lead to account termination and a liquidated damages fee.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">8. Conduct Rules</h2>
                <p className="mb-2">Do <strong>not</strong>:</p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• Post false, offensive, or infringing material</li>
                  <li>• Misrepresent your identity or licences</li>
                  <li>• Upload malware or imply DroneDen endorsement</li>
                </ul>
                <p>DroneDen may remove content or suspend accounts for violations or legal compliance.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">9. Location Restrictions</h2>
                <p>You are responsible for ensuring that your use of DroneDen is legal in your jurisdiction. We may block access where legally required.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">10. External Links</h2>
                <p>We may link to third-party websites. These are not endorsed or controlled by DroneDen and are visited at your own risk.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">11. Bookings & Payment via Stripe</h2>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• Bookings are managed via Stripe Connect or direct bank transfers</li>
                  <li>• Stripe pre-authorises payment upon booking</li>
                  <li>• <strong>80%</strong> is released to you after job approval (or auto-approval after 48h)</li>
                  <li>• <strong>20%</strong> is retained as our service fee</li>
                </ul>
                <p>You remain responsible for VAT and all taxes.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">12. Cancellations & Disputes</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• Bookings may be canceled under the displayed policy</li>
                  <li>• DroneDen is not liable for any job-related disputes</li>
                  <li>• Parties must attempt to resolve issues via platform messaging</li>
                  <li>• Jurisdiction: Cluj-Napoca courts, Romania</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">13. Ratings & Performance</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• Clients may rate you publicly</li>
                  <li>• You may not manipulate reviews or pressure clients</li>
                  <li>• Missing deadlines or lacking proper documentation is a breach and may result in a refund at your expense</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">14. Feedback System</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• Ratings and feedback may be displayed, edited, or hidden by DroneDen</li>
                  <li>• Feedback cannot be coerced or manipulated</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">15. Financial Terms</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• All payments flow through <strong>Stripe Payments Europe, Ltd.</strong></li>
                  <li>• Stripe Connect is mandatory</li>
                  <li>• DroneDen automatically deducts its service fee</li>
                  <li>• You must issue legally valid fiscal documents</li>
                  <li>• You're liable for all taxes (VAT, CAS, CASS, etc.)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">16. Fee Disputes & Non-Payment</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• Unpaid amounts may incur interest (1.5%/month or per Romanian Civil Code Art. 1535)</li>
                  <li>• We may withhold future payouts or charge your stored payment method</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">17. Insurance & Risk</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• You must carry active liability and hull insurance for each mission</li>
                  <li>• Our liability is limited to the service fee for the job in question</li>
                  <li>• We are not liable for indirect or consequential damages</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">18. Indemnification</h2>
                <p className="mb-2">You agree to indemnify DroneDen against claims or penalties from:</p>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• Breaching these Terms</li>
                  <li>• Violating laws (aviation, GDPR, etc.)</li>
                  <li>• Negligent or intentional misconduct</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">19. Disclaimers</h2>
                <p className="mb-2">DroneDen makes no guarantees that:</p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• The platform will always be available</li>
                  <li>• Clients will pay</li>
                  <li>• Jobs will meet commercial or legal expectations</li>
                </ul>
                <p>We operate "as-is" and "as-available", without prejudice to mandatory consumer rights.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">20. Limitation of Liability</h2>
                <p className="mb-2">Our total liability is capped at:</p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• The retained service fee for the disputed job, or</li>
                  <li>• RON 300 — whichever is higher</li>
                </ul>
                <p className="mb-2">Excludes:</p>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• Death or personal injury</li>
                  <li>• Gross negligence or fraud</li>
                  <li>• Loss of income, data, or reputation</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">21. Service Interruptions</h2>
                <p>Platform availability may be affected by updates or force majeure events. We are not liable for downtime or data loss.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">22. Content Licence</h2>
                <p>
                  By uploading content (text, images, logs), you grant DroneDen a <strong>worldwide, royalty-free licence</strong> to use it for platform operation and marketing. Your moral rights are preserved.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">23. Copyright Complaints</h2>
                <p className="mb-3">Send infringement notices to <a href="mailto:contact@droneden.ro" className="text-primary underline">contact@droneden.ro</a> including:</p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• Your contact details</li>
                  <li>• Description and location of the infringing material</li>
                  <li>• Statement of good-faith belief</li>
                  <li>• Electronic signature</li>
                </ul>
                <p>We will act per Law 365/2002.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">24. Termination</h2>
                <p className="mb-2">We may suspend or terminate your account due to:</p>
                <ul className="text-sm space-y-1 ml-6 mb-3">
                  <li>• Material breach</li>
                  <li>• Repeated poor feedback</li>
                  <li>• Legal issues</li>
                </ul>
                <p>Jobs already in progress will follow cancellation policy terms.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">25. Updates to Terms</h2>
                <p>We may modify these Terms at any time. Updates will be announced via the dashboard or email. Continued use implies acceptance.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">26. Legal Framework</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• <strong>Governing Law:</strong> Romanian law</li>
                  <li>• <strong>Jurisdiction:</strong> Courts of Cluj-Napoca</li>
                  <li>• All notices are valid if sent electronically</li>
                  <li>• These Terms supersede all prior agreements</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">27. Survival of Terms</h2>
                <p className="mb-2">The following survive termination:</p>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• Platform licence (Art. 5)</li>
                  <li>• Payment & fee clauses (Arts. 15–16)</li>
                  <li>• Content licence (Art. 22)</li>
                  <li>• Disclaimers & indemnities (Arts. 18–21)</li>
                  <li>• Legal jurisdiction (Art. 26)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">28. Stripe Services Agreement</h2>
                <p>By using DroneDen, you agree to Stripe's Connected Account Agreement and authorise us to share relevant data with Stripe.</p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-primary mb-3">29. Contact</h2>
                <ul className="text-sm space-y-1 ml-6">
                  <li>• 📧 <a href="mailto:contact@droneden.ro" className="text-primary underline">contact@droneden.ro</a></li>
                  <li>• 📞 +40 749 148 608</li>
                </ul>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsModal; 